import type { JsonObject, QuerygenChartConfig } from "../types";

export type PinboardPanelKind = "table" | "chart";

export type PinboardPanel = {
  id: string;
  title: string;
  kind: PinboardPanelKind;
  agentId: string;
  query: string;
  chartConfig?: QuerygenChartConfig | null;
  createdAt: string;
};

export type PinboardPanelResult = {
  query?: string;
  rows: JsonObject[];
  rowCount: number;
  truncated: boolean;
  refreshedAt: string;
  error?: string | null;
};

export type Pinboard = {
  id: string;
  name: string;
  panels: PinboardPanel[];
  createdAt: string;
  updatedAt: string;
};

const STORAGE_KEY_PREFIX = "datascope_pinboards";
const RESULT_STORAGE_KEY_PREFIX = "datascope_pinboard_panel_result";

const storageKey = (organizationId?: string | null): string =>
  organizationId ? `${STORAGE_KEY_PREFIX}:${organizationId}` : STORAGE_KEY_PREFIX;

const resultStorageKey = (
  panelId: string,
  organizationId?: string | null,
): string =>
  organizationId
    ? `${RESULT_STORAGE_KEY_PREFIX}:${organizationId}:${panelId}`
    : `${RESULT_STORAGE_KEY_PREFIX}:${panelId}`;

const makeId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const isPinboardArray = (value: unknown): value is Pinboard[] =>
  Array.isArray(value) &&
  value.every(
    (pinboard) =>
      pinboard &&
      typeof pinboard === "object" &&
      typeof (pinboard as Pinboard).id === "string" &&
      typeof (pinboard as Pinboard).name === "string" &&
      Array.isArray((pinboard as Pinboard).panels),
  );

const normalizePinboard = (pinboard: Pinboard): Pinboard => ({
  ...pinboard,
  panels: pinboard.panels.map((panel) => ({
    ...panel,
    agentId: typeof panel.agentId === "string" ? panel.agentId : "",
    query: typeof panel.query === "string" ? panel.query : "",
  })),
});

export const readPinboards = (organizationId?: string | null): Pinboard[] => {
  try {
    const raw = localStorage.getItem(storageKey(organizationId));
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as unknown;
    return isPinboardArray(parsed) ? parsed.map(normalizePinboard) : [];
  } catch {
    return [];
  }
};

export const writePinboards = (
  pinboards: Pinboard[],
  organizationId?: string | null,
): void => {
  localStorage.setItem(storageKey(organizationId), JSON.stringify(pinboards));
};

export const createPinboard = (name: string): Pinboard => {
  const now = new Date().toISOString();
  return {
    id: makeId(),
    name: name.trim() || "Untitled Pinboard",
    panels: [],
    createdAt: now,
    updatedAt: now,
  };
};

export const addPanelToPinboard = (
  pinboards: Pinboard[],
  pinboardId: string,
  panel: Omit<PinboardPanel, "id" | "createdAt">,
): { pinboards: Pinboard[]; panel: PinboardPanel } => {
  const now = new Date().toISOString();
  const nextPanel: PinboardPanel = {
    ...panel,
    id: makeId(),
    createdAt: now,
  };

  return {
    panel: nextPanel,
    pinboards: pinboards.map((pinboard) =>
      pinboard.id === pinboardId
        ? {
            ...pinboard,
            panels: [...pinboard.panels, nextPanel],
            updatedAt: now,
          }
        : pinboard,
    ),
  };
};

const isPanelResult = (value: unknown): value is PinboardPanelResult =>
  Boolean(value) &&
  typeof value === "object" &&
  Array.isArray((value as PinboardPanelResult).rows) &&
  typeof (value as PinboardPanelResult).rowCount === "number" &&
  typeof (value as PinboardPanelResult).truncated === "boolean" &&
  typeof (value as PinboardPanelResult).refreshedAt === "string";

export const readPanelResult = (
  panelId: string,
  organizationId?: string | null,
): PinboardPanelResult | null => {
  try {
    const raw = localStorage.getItem(resultStorageKey(panelId, organizationId));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as unknown;
    return isPanelResult(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const readPanelResults = (
  panelIds: string[],
  organizationId?: string | null,
): Record<string, PinboardPanelResult> => {
  const results: Record<string, PinboardPanelResult> = {};
  panelIds.forEach((panelId) => {
    const result = readPanelResult(panelId, organizationId);
    if (result) {
      results[panelId] = result;
    }
  });
  return results;
};

export const writePanelResult = (
  panelId: string,
  result: PinboardPanelResult,
  organizationId?: string | null,
): void => {
  localStorage.setItem(
    resultStorageKey(panelId, organizationId),
    JSON.stringify(result),
  );
};

export const removePanelResult = (
  panelId: string,
  organizationId?: string | null,
): void => {
  localStorage.removeItem(resultStorageKey(panelId, organizationId));
};

export const removePanelFromPinboard = (
  pinboards: Pinboard[],
  pinboardId: string,
  panelId: string,
): Pinboard[] =>
  pinboards.map((pinboard) =>
    pinboard.id === pinboardId
      ? {
          ...pinboard,
          panels: pinboard.panels.filter((panel) => panel.id !== panelId),
          updatedAt: new Date().toISOString(),
        }
      : pinboard,
  );
